import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch, useSelector } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';
import { postDataAPI } from '../../utils/fetchData';
import { remindersApi } from '../../utils/remindersApi';

const QuickReminder = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userArticle } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTeam, setLoadingTeam] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        due_date: '',
        assignee: '',
        auto_reminder: 'on_due_date' // none, 1_day_before, on_due_date
    });

    // Follow-up reminders state
    const [followUpReminders, setFollowUpReminders] = useState([
        { days: '30', time: '8am' },  // Follow Up 1 - furthest out
        { days: '14', time: '8am' },  // Follow Up 2 - middle
        { days: '1', time: '8am' }    // Follow Up 3 - closest
    ]);

    // Generate days options: Day of + 1-60 days
    const daysOptions = ['day_of', ...Array.from({ length: 60 }, (_, i) => (i + 1).toString())];
    
    // Generate time options: 12am-12pm
    const timeOptions = [
        '12am', '1am', '2am', '3am', '4am', '5am', 
        '6am', '7am', '8am', '9am', '10am', '11am', '12pm'
    ];

    // Fetch team members on component mount
    useEffect(() => {
        fetchTeamMembers();
    }, []);

    const fetchTeamMembers = async () => {
        setLoadingTeam(true);
        try {
            const token = localStorage.getItem('dizeeToken');
            const response = await postDataAPI('team/members', {
                userId: userArticle?._id,
                userRole: userArticle?.professionRole
            }, token);

            if (response.data?.status === 200) {
                const members = response.data.data || [];
                // Add current user as an option
                const currentUser = {
                    id: 'self',
                    name: userArticle?.username || 'Me',
                    role: userArticle?.professionRole || 'Self',
                    email: userArticle?.email
                };
                setTeamMembers([currentUser, ...members]);
            } else {
                // Fallback to just current user if team fetch fails
                const currentUser = {
                    id: 'self',
                    name: userArticle?.username || 'Me',
                    role: userArticle?.professionRole || 'Self',
                    email: userArticle?.email
                };
                setTeamMembers([currentUser]);
            }
        } catch (error) {
            console.error('Error fetching team members:', error);
            // Fallback to just current user
            const currentUser = {
                id: 'self',
                name: userArticle?.username || 'Me',
                role: userArticle?.professionRole || 'Self',
                email: userArticle?.email
            };
            setTeamMembers([currentUser]);
        } finally {
            setLoadingTeam(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFollowUpChange = (index, field, value) => {
        setFollowUpReminders(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            // Validation
            if (!formData.title.trim()) {
                dispatch(setErrorAlert('Please enter a title'));
                setIsLoading(false);
                return;
            }

            if (!formData.due_date) {
                dispatch(setErrorAlert('Please select a due date'));
                setIsLoading(false);
                return;
            }

            if (!formData.assignee) {
                dispatch(setErrorAlert('Please select a team member'));
                setIsLoading(false);
                return;
            }

            // Create the quick reminder data
            const selectedMember = teamMembers.find(member => member.id === formData.assignee);
            const quickReminderData = {
                title: formData.title,
                description: `Quick reminder assigned to ${selectedMember?.name || selectedMember?.username}`,
                due_date: formData.due_date,
                priority: 'medium',
                category: 'quick',
                type: 'quick',
                assignee_name: selectedMember?.name || selectedMember?.username,
                assignee_role: selectedMember?.role || selectedMember?.professionRole,
                assignee_email: selectedMember?.email,
                auto_reminder: formData.auto_reminder,
                follow_up_reminders: followUpReminders,
                quick_reminder: true
            };

            // Check if this is a release workflow (could be expanded to detect this)
            const isReleaseWorkflow = quickReminderData.category === 'release' || 
                                    quickReminderData.title.toLowerCase().includes('release');
            
            // Try to create via API first, fallback to local storage
            const apiResult = await remindersApi.createCustomReminder(quickReminderData);
            
            if (apiResult.success) {
                if (isReleaseWorkflow && window.showReminderFlowModal) {
                    window.showReminderFlowModal(quickReminderData.title, 1);
                } else {
                    dispatch(setSuccessAlert('Quick reminder created successfully'));
                }
            } else {
                console.log('API creation failed, using local storage fallback:', apiResult.error);
                
                // Fallback to local storage method
                const localQuickReminder = {
                    id: `quick_${Date.now()}`,
                    ...quickReminderData,
                    metadata: {
                        assignee_name: selectedMember?.name || selectedMember?.username,
                        assignee_role: selectedMember?.role || selectedMember?.professionRole,
                        assignee_email: selectedMember?.email,
                        auto_reminder: formData.auto_reminder,
                        follow_up_reminders: followUpReminders,
                        quick_reminder: true,
                        created_at: new Date().toISOString()
                    },
                    status: 'pending',
                    viewed: false
                };

                // Add quick reminder using the local reminders manager
                const { addCustomReminder } = await import('../../utils/remindersData');
                const localResult = addCustomReminder(localQuickReminder);
                
                if (localResult) {
                    dispatch(setSuccessAlert('Quick reminder created successfully (offline)'));
                } else {
                    throw new Error('Failed to create reminder locally');
                }
            }

            navigate('/reminders');
        } catch (error) {
            console.error('Error creating quick reminder:', error);
            dispatch(setErrorAlert('Failed to create reminder'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/reminders');
    };

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Add Quick Reminder</span>
                        <div className="flex items-center gap-4">
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate('/reminders')}
                            >
                                View All
                            </span>
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate(-1)}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        <div className="flex flex-col">
                            <div className="mb-6">
                                <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Create a quick reminder</span>
                                <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">
                                    Set up simple reminders for tasks and assign them to team members. 
                                    Perfect for quick tasks and deadlines.
                                </p>
                            </div>

                            {/* Form Fields */}
                            <div className="space-y-4 mb-6">
                                
                                {/* Title Field */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Title
                                        <span className="text-red-400 ml-1">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        placeholder="Enter a title..."
                                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                        required
                                    />
                                </div>

                                {/* Due Date Field */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Due Date
                                        <span className="text-red-400 ml-1">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.due_date}
                                        onChange={(e) => handleInputChange('due_date', e.target.value)}
                                        className="w-full bg-transparent border-none outline-none text-white text-[12px]"
                                        required
                                    />
                                </div>

                                {/* Assign To Field */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Assign To
                                        <span className="text-red-400 ml-1">*</span>
                                    </label>
                                    <select
                                        value={formData.assignee}
                                        onChange={(e) => handleInputChange('assignee', e.target.value)}
                                        disabled={loadingTeam}
                                        className="w-full bg-transparent border-none outline-none text-white text-[12px] cursor-pointer disabled:opacity-50"
                                        required
                                    >
                                        <option value="" disabled className="bg-black">
                                            {loadingTeam ? 'Loading team members...' : 'Select team member...'}
                                        </option>
                                        {teamMembers.map(member => (
                                            <option 
                                                key={member.id} 
                                                value={member.id} 
                                                className="bg-black text-white"
                                            >
                                                {member.name || member.username} - {member.role || member.professionRole}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Follow Up Reminders Section */}
                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all">
                                    <label className="block text-white text-[12px] font-semibold mb-2">
                                        Follow Up Reminders
                                    </label>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mb-4">
                                        Based on the final task due date you set in the previous step. There will always be a auto reminder the day of the due date.
                                    </p>
                                    
                                    <div className="space-y-3">
                                        {followUpReminders.map((reminder, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <span className="text-white text-[11px] w-20">Follow Up {index + 1}</span>
                                                
                                                {/* Days Dropdown */}
                                                <select
                                                    value={reminder.days}
                                                    onChange={(e) => handleFollowUpChange(index, 'days', e.target.value)}
                                                    className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[11px] cursor-pointer outline-none focus:border-[#FF3B30]"
                                                >
                                                    <option value="day_of" className="bg-black">Day of</option>
                                                    {Array.from({ length: 60 }, (_, i) => i + 1).map(day => (
                                                        <option key={day} value={day.toString()} className="bg-black">
                                                            {day} {day === 1 ? 'day' : 'days'}
                                                        </option>
                                                    ))}
                                                </select>

                                                {/* Time Dropdown */}
                                                <select
                                                    value={reminder.time}
                                                    onChange={(e) => handleFollowUpChange(index, 'time', e.target.value)}
                                                    className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[11px] cursor-pointer outline-none focus:border-[#FF3B30]"
                                                >
                                                    {timeOptions.map(time => (
                                                        <option key={time} value={time} className="bg-black">
                                                            {time}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Save Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all">
                                <button
                                    onClick={handleSave}
                                    disabled={isLoading}
                                    className="w-full bg-[#FF3B30] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(255,59,48,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Creating Reminder...
                                        </>
                                    ) : (
                                        'Create Reminder'
                                    )}
                                </button>
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default QuickReminder;
